/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Container,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import { Page } from 'src/components';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { LoaderBar } from 'src/components';
import { debounce, rest } from 'lodash';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { Audit, AuditListPayload } from 'src/types';
import { AuditToolbar } from './component/toolbar';
import { useAudit } from 'src/hooks/audits';
import clsx from 'clsx';
import { Pagination } from '@material-ui/lab';
import { json } from 'stream/consumers';
import { useSearchParams } from 'react-router-dom';

const { actions: auditActions, selectors: auditSelectors } = slices.audit;

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100%'
  },
  tableCell: {
    textAlign: 'left',
    verticalAlign: 'top'
  },
  productCard: {
    height: '100%'
  },
  noWrapCell: {
    whiteSpace: 'nowrap'
  }
}));

const AuditView = () => {
  const {
    isLoading,
    fetchAuditList,
    paginationMeta,
    auditList,
    setAuditListParams
  } = useAudit();

  const classes = useStyles({ auditCount: auditList?.length ?? 0 });
  const [searchParams] = useSearchParams();
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [filterApplied, setFilterApplied] = useState(false);
  const isSinglePage = useMemo(() => {
  return !paginationMeta.links.next && !paginationMeta.links.prev;
}, [paginationMeta])

  const handleApplyFilter = (params: AuditListPayload) => {
    setAuditListParams(params);
    fetchAuditList(params);
    setFilterApplied(true);
    setPageNumber(1);
  };

  const onHandleChangePage = (page?: number) => {
    setPageNumber(page || 1);
    fetchAuditList({
      auditable_id: Number(searchParams.get('auditable_id')) || 0,
      page: page || 1
    });

    window.scrollTo(0, 0);
  };

  return (
    <Page className={classes.root} title="Audits">
      <Container maxWidth={false}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
        >
          <Typography color="textPrimary" gutterBottom variant="h3">
            Audits
          </Typography>
          <AuditToolbar onHandleAppliedFilter={handleApplyFilter} />
        </Box>
        {filterApplied && !isSinglePage && (
          <Box sx={{ mt: '1em', display: 'flex', justifyContent: 'end' }}>
            <Pagination
              page={pageNumber}
              onChange={(event, page) => onHandleChangePage(page)}
              count={pageNumber + (paginationMeta?.links?.next ? 1 : 0)}
              siblingCount={1}
              boundaryCount={1}
              showFirstButton={false}
              showLastButton={false}
              hidePrevButton={!paginationMeta?.links?.prev} // Hide if no previous page
              hideNextButton={!paginationMeta?.links?.next}
            />
          </Box>
        )}
        <Box mt={2}>
          <LoaderBar isLoading={isLoading} />
          <Card className={classes.root} {...rest}>
            <PerfectScrollbar>
              <Box>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        className={classes.noWrapCell}
                        style={{ display: 'none' }}
                      >
                        Auditable ID
                      </TableCell>
                      <TableCell className={classes.noWrapCell}>
                        Auditable Type
                      </TableCell>
                      <TableCell className={classes.noWrapCell}>
                        Performed By
                      </TableCell>
                      <TableCell className={classes.noWrapCell}>
                        User Type
                      </TableCell>
                      <TableCell>Endpoint</TableCell>
                      <TableCell>Event</TableCell>
                      <TableCell className={classes.noWrapCell}>
                        Old Values
                      </TableCell>
                      <TableCell className={classes.noWrapCell}>
                        New Values
                      </TableCell>
                      <TableCell className={classes.noWrapCell}>
                        Audit Date
                      </TableCell>
                      <TableCell className={classes.noWrapCell}>
                        Complete Details
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditList?.map((item: Audit) => (
                      <TableRow hover key={item.audit_id}>
                        <TableCell
                          className={classes.tableCell}
                          style={{ display: 'none' }}
                        >
                          {item?.audit_id}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {item?.auditable_type}
                        </TableCell>
                        <TableCell
                          className={clsx(
                            classes.noWrapCell,
                            classes.tableCell
                          )}
                        >
                          {item?.performed_by}
                        </TableCell>
                        <TableCell
                          className={clsx(
                            classes.noWrapCell,
                            classes.tableCell
                          )}
                        >
                          {item?.user_type || '-'}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {item?.endpoint}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {item?.event}
                        </TableCell>

                        <TableCell
                          className={classes.tableCell}
                          style={{
                            minWidth:
                              item?.old_values &&
                              Object.keys(item.old_values).length > 0
                                ? 400
                                : 'auto'
                          }}
                        >
                          <pre
                            style={{
                              margin: 0,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'normal',
                              overflowWrap: 'anywhere',
                              fontFamily: 'monospace'
                            }}
                          >
                            {item?.old_values
                              ? JSON.stringify(item.old_values, null, 2)
                              : ''}
                          </pre>
                        </TableCell>

                        <TableCell
                          className={classes.tableCell}
                          style={{ minWidth: 500 }}
                        >
                          <pre
                            style={{
                              margin: 0,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'normal',
                              overflowWrap: 'anywhere',
                              fontFamily: 'monospace'
                            }}
                          >
                            {JSON.stringify(item?.new_values, null, 2)}
                          </pre>
                        </TableCell>

                        <TableCell
                          className={clsx(
                            classes.noWrapCell,
                            classes.tableCell
                          )}
                        >
                          {item?.updated_at}
                        </TableCell>

                        <TableCell
                          className={classes.tableCell}
                          style={{
                            minWidth: item.auditable != null ? 500 : 'auto'
                          }}
                        >
                          <pre
                            style={{
                              margin: 0,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'normal',
                              overflowWrap: 'anywhere',
                              fontFamily: 'monospace'
                            }}
                          >
                            {JSON.stringify(item.auditable, null, 2)}
                          </pre>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </PerfectScrollbar>
          </Card>
        </Box>
        {filterApplied && !isSinglePage && (
          <Box
            sx={{
              mt: '1em',
              mb: '1em',
              display: 'flex',
              justifyContent: 'end'
            }}
          >
            <Pagination
              page={pageNumber}
              onChange={(event, page) => onHandleChangePage(page)}
              count={pageNumber + (paginationMeta?.links?.next ? 1 : 0)}
              siblingCount={1}
              boundaryCount={1}
              showFirstButton={false}
              showLastButton={false}
              hidePrevButton={!paginationMeta?.links?.prev} // Hide if no previous page
              hideNextButton={!paginationMeta?.links?.next}
            />
          </Box>
        )}
      </Container>
    </Page>
  );
};

export default AuditView;
